// Copyright (c) 2026 Yunus YILDIZ — SPDX-License-Identifier: BUSL-1.1

import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Building2, Check, ChevronsUpDown } from 'lucide-react'
import { useAuth } from '@/lib/auth/useAuth'
import { authExtraApi } from '@/lib/api/authExtra'
import { queryKeys } from '@/constants/queryKeys'
import { Button } from '@/components/ui/Button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu'
import { cn } from '@/lib/utils/cn'

async function invalidateTenantScopedQueries(queryClient: ReturnType<typeof useQueryClient>) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['account'] }),
    queryClient.invalidateQueries({ queryKey: ['documents'] }),
    queryClient.invalidateQueries({ queryKey: ['apiKeys'] }),
    queryClient.invalidateQueries({ queryKey: ['webhooks'] }),
    queryClient.invalidateQueries({ queryKey: ['billing'] }),
    queryClient.invalidateQueries({ queryKey: ['auditLog'] }),
    queryClient.invalidateQueries({ queryKey: ['schemas'] }),
  ])
}

export function WorkspaceSwitcher() {
  const { user, applyAuthResult } = useAuth()
  const queryClient = useQueryClient()
  const [busy, setBusy] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.auth.workspaces(),
    queryFn: () => authExtraApi.getWorkspaces(),
    enabled: Boolean(user),
    staleTime: 60_000,
  })

  const workspaces = data?.workspaces ?? []
  const current = workspaces.find((w) => w.tenantId === user?.tenantId)

  if (!user || isLoading || workspaces.length < 2) return null

  async function switchTo(tenantId: string) {
    if (!user || tenantId === user.tenantId || busy) return
    setBusy(true)
    try {
      const result = await authExtraApi.switchWorkspace(tenantId)
      applyAuthResult(result.tokens, result.user)
      await queryClient.invalidateQueries({ queryKey: queryKeys.auth.workspaces() })
      await invalidateTenantScopedQueries(queryClient)
    } finally {
      setBusy(false)
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            'h-8 max-w-[140px] sm:max-w-[220px] justify-between gap-1 px-2 font-normal',
            busy && 'opacity-70',
          )}
          disabled={busy}
          aria-label="Switch organization"
        >
          <Building2 className="h-3.5 w-3.5 shrink-0 text-ink-muted" />
          <span className="truncate text-xs">{current?.organization ?? 'Organization'}</span>
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-ink-muted" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[260px]">
        {workspaces.map((w) => (
          <DropdownMenuItem
            key={w.tenantId}
            onSelect={() => void switchTo(w.tenantId)}
            className="flex items-center justify-between gap-2"
          >
            <span className="truncate">{w.organization}</span>
            <span className="flex items-center gap-1 shrink-0 text-xs text-ink-muted">
              {w.role}
              {w.tenantId === user.tenantId && <Check className="h-3.5 w-3.5 text-brand" />}
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
